<template>
    <CustomDialog v-model="visible" :defaultTitle="$t('products.filters.title')" @confirm="applyFilters" @cancel="$emit('close')">
        <template #content>
            <div class="ppv2-gap-6 ppv2-px-1 ppv2-space-y-4">
                <!-- Colonne 1 -->
                <div
                    class="ppv2-flex md:ppv2-flex-row ppv2-flex-col ppv2-items-center md:ppv2-items-start ppv2-flex-1 ppv2-gap-3 ppv2-justify-between"
                >
                    <div class="ppv2-flex-1 ppv2-w-full">
                        <BaseSelect
                            v-model="localFilters.categories"
                            :label="$t('products.filters.categories')"
                            :options="availableFilters.categories"
                            optionLabel="name"
                            optionValue="id"
                            :multiple="true"
                            :placeholder="$t('products.filters.selectCategories')"
                        />
                    </div>

                    <div class="ppv2-flex-1 ppv2-w-full">
                        <BaseSelect
                            v-model="localFilters.subcategories"
                            :label="$t('products.filters.subcategories')"
                            :options="availableFilters.subcategories"
                            optionLabel="name"
                            optionValue="id"
                            :multiple="true"
                            :placeholder="$t('products.filters.selectSubcategories')"
                        />
                    </div>

                    <div class="ppv2-flex-1 ppv2-w-full">
                        <BaseSelect
                            v-model="localFilters.brands"
                            :label="$t('products.filters.brands')"
                            :options="availableFilters.brands"
                            optionLabel="name"
                            optionValue="id"
                            :multiple="true"
                            :placeholder="$t('products.filters.selectBrands')"
                        />
                    </div>
                </div>

                <!-- Colonne 2 -->
                <div
                    class="ppv2-flex md:ppv2-flex-row ppv2-flex-col ppv2-items-center md:ppv2-items-start ppv2-flex-1 ppv2-gap-3 ppv2-justify-between"
                >
                    <div class="ppv2-flex-1 ppv2-w-full">
                        <BaseSelect
                            v-model="localFilters.suppliers"
                            :label="$t('products.filters.suppliers')"
                            :options="availableFilters.suppliers"
                            optionLabel="name"
                            optionValue="id"
                            :multiple="true"
                            :placeholder="$t('products.filters.selectSuppliers')"
                        />
                    </div>

                    <div class="ppv2-flex-1 ppv2-w-full ppv2-space-y-2">
                        <label class="ppv2-block ppv2-text-sm poppinsMedium ppv2-text-gray-700">
                            {{ $t('products.filters.priceRange') }}
                        </label>
                        <div class="ppv2-grid ppv2-grid-cols-2 ppv2-gap-2">
                            <CustomInput
                                v-model.number="localFilters.price.greater_than"
                                type="number"
                                :placeholder="$t('products.filters.minPrice')"
                                :rightIcon="$symbolCurrency"
                            />
                            <CustomInput
                                v-model.number="localFilters.price.lesser_than"
                                type="number"
                                :placeholder="$t('products.filters.maxPrice')"
                                :rightIcon="$symbolCurrency"
                            />
                        </div>
                    </div>
                    <div class="ppv2-flex-1 ppv2-w-full">
                        <BaseSelect
                            v-model="localFilters.inventory"
                            :label="$t('products.filters.inventory')"
                            :options="inventoryOptions"
                            :placeholder="$t('products.filters.selectInventory')"
                            :clearable="true"
                        />
                    </div>
                </div>

                <!-- Colonne 3 -->
                <div
                    class="ppv2-flex md:ppv2-flex-row ppv2-flex-col ppv2-items-center md:ppv2-items-start ppv2-flex-1 ppv2-gap-3 ppv2-justify-between"
                >
                    <div class="ppv2-flex-1 ppv2-w-full">
                        <BaseSelect
                            v-model="localFilters.active"
                            :label="$t('products.filters.status')"
                            :options="statusOptions"
                            :placeholder="$t('products.filters.selectStatus')"
                            :clearable="true"
                        />
                    </div>
                    <div class="ppv2-flex-1 ppv2-w-full">
                        <BaseSelect
                            v-model="localFilters.visible_online"
                            :label="$t('products.filters.visibleOnline')"
                            :options="visibilityOptions"
                            :placeholder="$t('products.filters.selectVisibility')"
                            :clearable="true"
                        />
                    </div>
                    <div class="ppv2-flex-1 ppv2-w-full md:ppv2-flex md:ppv2-hidden">
                        <!--div class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-pt-2">
                            <BaseCheckbox v-model="localFilters.is_cabin" id="is_cabin" :true-value="1" :false-value="null" />
                            <label for="is_cabin" class="ppv2-text-sm poppinsMedium ppv2-text-gray-700 ppv2-cursor-pointer">
                                {{ $t('products.filters.isCabin') }}
                            </label>
                        </div-->
                    </div>
                </div>
            </div>

            <!-- Info -->
            <div class="ppv2-mt-6 ppv2-bg-gray-50 ppv2-p-3 ppv2-rounded-lg ppv2-flex ppv2-justify-between ppv2-items-center">
                <div class="ppv2-text-sm ppv2-text-gray-700">
                    <span class="poppinsBold ppv2-text-blue-600">{{ activeFiltersCount }}</span>
                    {{ $t('common.activeFilters') }}
                </div>
                <button
                    v-if="activeFiltersCount > 0"
                    @click="resetFilters"
                    class="ppv2-text-xs ppv2-text-red-600 hover:ppv2-text-red-800 hover:ppv2-underline"
                >
                    {{ $t('common.resetAll') }}
                </button>
            </div>
        </template>

        <template #actions>
            <BaseButton variant="primary" @click="applyFilters" customClass="ppv2-px-8">
                {{ $t('products.filters.apply') }}
            </BaseButton>
        </template>
    </CustomDialog>
</template>

<script>
import CustomDialog from '../../Shared/CustomDialog.vue'
import BaseSelect from '../../Shared/BaseSelect.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import CustomInput from '../../Inputs/CustomInput.vue'
import BaseCheckbox from '../../Shared/BaseCheckbox.vue'

export default {
    name: 'ProductAdvancedFiltersV3',
    components: {
        CustomDialog,
        BaseSelect,
        BaseButton,
        CustomInput,
        BaseCheckbox,
    },
    props: {
        availableFilters: {
            type: Object,
            required: true,
        },
        currentFilters: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            visible: true,
            localFilters: {
                categories: [],
                subcategories: [],
                brands: [],
                suppliers: [],
                price: {
                    greater_than: null,
                    lesser_than: null,
                },
                inventory: null,
                active: null,
                visible_online: null,
                is_cabin: null,
            },
            inventoryOptions: [
                { label: this.$t('products.filters.inventoryAlert'), value: 'alert' },
                { label: this.$t('products.filters.inventoryOut'), value: 'out' },
                { label: this.$t('products.filters.inventoryOrdering'), value: 'ordering' },
            ],
            statusOptions: [
                { label: this.$t('products.status.active'), value: 1 },
                { label: this.$t('products.status.inactive'), value: 0 },
            ],
            visibilityOptions: [
                { label: this.$t('products.status.visible'), value: 1 },
                { label: this.$t('products.status.hidden'), value: 0 },
            ],
        }
    },
    mounted() {
        if (this.currentFilters && typeof this.currentFilters === 'object' && !Array.isArray(this.currentFilters)) {
            // Merge current filters, but ensure nested price object is also merged
            this.localFilters = {
                ...this.localFilters,
                ...this.currentFilters,
                price: {
                    ...this.localFilters.price,
                    ...(this.currentFilters.price || {}),
                },
            }
        }
    },
    computed: {
        activeFiltersCount() {
            let count = 0
            if (this.localFilters.categories?.length) count++
            if (this.localFilters.subcategories?.length) count++
            if (this.localFilters.brands?.length) count++
            if (this.localFilters.suppliers?.length) count++
            if (this.localFilters.price.greater_than !== null && this.localFilters.price.greater_than !== '') count++
            if (this.localFilters.price.lesser_than !== null && this.localFilters.price.lesser_than !== '') count++
            if (this.localFilters.inventory) count++
            if (this.localFilters.active !== null) count++
            if (this.localFilters.visible_online !== null) count++
            if (this.localFilters.is_cabin) count++
            return count
        },
    },
    methods: {
        resetFilters() {
            this.localFilters = {
                categories: [],
                subcategories: [],
                brands: [],
                suppliers: [],
                price: {
                    greater_than: null,
                    lesser_than: null,
                },
                inventory: null,
                active: null,
                visible_online: null,
                is_cabin: null,
            }
        },
        applyFilters() {
            this.$emit('update', JSON.parse(JSON.stringify(this.localFilters)))
            this.$emit('close')
        },
    },
}
</script>

<style scoped>
:deep(.custom-input-wrapper) {
    margin-bottom: 0 !important;
}
</style>
